"use client";

/**
 * Firebase client SDK init.
 *
 * Constitution Principle II: Firebase is the FRONT half of authentication.
 * We sign in here to obtain an ID token, then immediately POST it to the BFF
 * which exchanges it for an HttpOnly Perl session cookie. The token is never
 * stored beyond the sign-in dance.
 *
 * Same Firebase project as MD3-Admin Flutter / MD3-APP / MD3hub so a single
 * employee record (one email) authenticates across all three apps.
 */

import { type FirebaseApp, getApps, initializeApp } from "firebase/app";
import {
  type Auth,
  GoogleAuthProvider,
  OAuthProvider,
  getAuth,
  signInWithEmailAndPassword,
  signInWithPopup,
  type UserCredential,
} from "firebase/auth";

const firebaseConfig = {
  apiKey: process.env.NEXT_PUBLIC_FIREBASE_API_KEY,
  authDomain: process.env.NEXT_PUBLIC_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.NEXT_PUBLIC_FIREBASE_PROJECT_ID,
  storageBucket: process.env.NEXT_PUBLIC_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.NEXT_PUBLIC_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.NEXT_PUBLIC_FIREBASE_APP_ID,
};

let app: FirebaseApp | null = null;
let auth: Auth | null = null;

function getFirebaseAuth(): Auth {
  if (auth) return auth;
  app = getApps()[0] ?? initializeApp(firebaseConfig);
  auth = getAuth(app);
  return auth;
}

export type FirebaseSignInResult =
  | { kind: "ok"; email: string; idToken: string }
  | { kind: "cancelled" }
  | { kind: "error"; code: string; message: string };

/**
 * Email/password sign-in via Firebase. Returns the email + ID token to
 * forward to the BFF, or a structured error suitable for surfacing in the
 * login form.
 *
 * Errors are mapped to Chinese messages here so the LoginForm can stay
 * presentation-only.
 */
export async function signInWithEmail(
  email: string,
  password: string,
): Promise<FirebaseSignInResult> {
  try {
    const fbAuth = getFirebaseAuth();
    const cred = await signInWithEmailAndPassword(fbAuth, email, password);
    const user = cred.user;
    if (!user.email) {
      return {
        kind: "error",
        code: "no-email",
        message: "登入帳號缺少 Email，請聯繫管理員",
      };
    }
    const idToken = await user.getIdToken();
    return { kind: "ok", email: user.email, idToken };
  } catch (e) {
    return mapFirebaseError(e);
  }
}

/**
 * Sign in with Google via Firebase popup. Returns the Google account's
 * email + ID token to forward to the BFF. The BFF flow is identical to
 * email/password sign-in.
 *
 * Cancellation (user closed popup, browser blocked it) is reported as
 * `kind: "cancelled"` so the UI can stay quiet — it isn't an error.
 */
export async function signInWithGoogle(): Promise<FirebaseSignInResult> {
  return signInWithProvider(new GoogleAuthProvider());
}

/**
 * Sign in with Apple via Firebase popup. Same shape as Google.
 * Apple's email may be a private relay address; that's fine — Perl
 * will accept or reject based on the local employee table.
 */
export async function signInWithApple(): Promise<FirebaseSignInResult> {
  const provider = new OAuthProvider("apple.com");
  provider.addScope("email");
  provider.addScope("name");
  return signInWithProvider(provider);
}

async function signInWithProvider(
  provider: GoogleAuthProvider | OAuthProvider,
): Promise<FirebaseSignInResult> {
  try {
    const fbAuth = getFirebaseAuth();
    const cred: UserCredential = await signInWithPopup(fbAuth, provider);
    const user = cred.user;
    if (!user.email) {
      return {
        kind: "error",
        code: "no-email",
        message: "登入帳號缺少 Email，請聯繫管理員",
      };
    }
    const idToken = await user.getIdToken();
    return { kind: "ok", email: user.email, idToken };
  } catch (e) {
    return mapPopupError(e);
  }
}

function mapPopupError(err: unknown): FirebaseSignInResult {
  const code =
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    typeof (err as { code: unknown }).code === "string"
      ? (err as { code: string }).code
      : "unknown";
  // Cancellation is not an error.
  if (
    code === "auth/popup-closed-by-user" ||
    code === "auth/cancelled-popup-request" ||
    code === "auth/user-cancelled"
  ) {
    return { kind: "cancelled" };
  }
  return mapFirebaseError(err);
}

/**
 * Sign out from Firebase. Called after the BFF logout completes so the
 * client is consistent. Server-side session is the source of truth; this
 * is best-effort cleanup.
 */
export async function signOutFromFirebase(): Promise<void> {
  try {
    const fbAuth = getFirebaseAuth();
    if (fbAuth.currentUser) {
      const { signOut } = await import("firebase/auth");
      await signOut(fbAuth);
    }
  } catch {
    // best-effort
  }
}

function mapFirebaseError(err: unknown): FirebaseSignInResult {
  const code =
    typeof err === "object" &&
    err !== null &&
    "code" in err &&
    typeof (err as { code: unknown }).code === "string"
      ? (err as { code: string }).code
      : "unknown";

  const messages: Record<string, string> = {
    "auth/invalid-email": "Email 格式不正確",
    "auth/user-disabled": "帳號已停用，請聯繫管理員",
    "auth/user-not-found": "帳號或密碼錯誤",
    "auth/wrong-password": "帳號或密碼錯誤",
    "auth/invalid-credential": "帳號或密碼錯誤",
    "auth/too-many-requests": "嘗試次數過多，請稍後再試",
    "auth/network-request-failed": "網路異常，請重試",
    "auth/internal-error": "Firebase 暫時無法使用",
    "auth/popup-blocked": "請允許此網站開啟新視窗",
    "auth/account-exists-with-different-credential":
      "此 Email 已綁定其他登入方式，請改用原本的登入方式",
    "auth/operation-not-allowed":
      "此登入方式尚未啟用，請聯繫管理員",
    "auth/unauthorized-domain":
      "此網域未獲授權使用此登入方式，請聯繫管理員",
  };

  return {
    kind: "error",
    code,
    message: messages[code] ?? "登入失敗，請稍後再試",
  };
}
